import React, { useRef, useState } from "react";
import { Box, TextField, Button, Grid, Typography } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import "./social-worker-information.css";
import PhoneInput from "react-phone-input-2";
import ImageUploadBoxSingle from "../../../../../common/imageUploadBox/Image-upload-box-single";


// Validation Schema using Yup
const validationSchema = Yup.object({
  socialWorkerName: Yup.string().required("Social Worker Name is Required"),
  socialWorkerEmail: Yup.string().email("Invalid email format").required("Social Worker Email is Required"),
  socialWorkerPhone: Yup.string().required("Social Worker Phone is Required"),
});

const SocialWorkerInformation = ({ updateFormData, nextStep, prevStep }) => {
  const webcamRefs = useRef([]); // Reference for webcam
  const fileInputRefs = useRef([]); // Reference for file input elements
  const [openIndexes, setOpenIndexes] = useState([]); // Tracks which webcam is open
  const [imageSrcs, setImageSrcs] = useState([]); // Array to store uploaded or captured image per index

  // Handle the file input change for single image upload
  const handleFileChange = (event, index) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrcs((prev) => {
          const updatedImages = [...prev];
          updatedImages[index] = reader.result; // Only one image per index
          return updatedImages;
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Function to capture the image from the webcam
  const capture = (index) => {
    if (webcamRefs.current[index]) {
      const image = webcamRefs.current[index].getScreenshot();
      setImageSrcs((prev) => {
        const updatedImages = [...prev];
        updatedImages[index] = image; // Store the captured image for this index
        return updatedImages;
      });
    }
  };

  const handleButtonClick = (index) => {
    fileInputRefs.current[index].click(); // Trigger file input click
  };

  return (
    <Box className='social-container-form'>
      <Box className='top-right-character'>
        <img src='./CYPImages/WormisDryStem.png' alt='Top Right Character' className='top-right-character-image' />
      </Box>

      <Formik
        initialValues={{
          socialWorkerName: "",
          socialWorkerEmail: "",
          socialWorkerPhone: "",
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          // Merge the image into socialWorkerInfo
          const socialWorkerInfo = {
            ...values,
            image: imageSrcs[0] || "", // Add the image to the socialWorkerInfo
          };

          // Send the combined data to updateFormData
          updateFormData({ socialWorkerInfo });
          nextStep(); // Move to next form
        }}
        validateOnMount={false}
        validateOnBlur={true}
      >
        {({ values, handleChange, handleBlur, setFieldValue, handleSubmit, errors, touched }) => (
          <Box mt={8} mb={6}>
            <Form onSubmit={handleSubmit} className='social-form-container'>
              {/* Form Title */}
              <Typography variant='h4' className='form-title' sx={{ color: "#1976d2", textAlign: "center" }}>
                My Social Worker
              </Typography>

              <Typography variant='body2' className='form-subtitle' sx={{ textAlign: "center", mt: 1 }}>
                You have met your social worker, and she will always be informed and updated on every plan, and
                activities about you. We will complete a detailed weekly report which would be shared with her.
              </Typography>

              {/* Drawing Section */}
              <Box sx={{ textAlign: "center", mt: 3, ml: 12, mr: 10 }}>
                <ImageUploadBoxSingle
                  key={0}
                  index={0}
                  imageSrc={imageSrcs[0] || ""} // Pass the single image per index
                  openIndexes={openIndexes}
                  webcamRef={webcamRefs}
                  fileInputRef={fileInputRefs}
                  handleButtonClick={handleButtonClick}
                  handleFileChange={handleFileChange}
                  capture={capture}
                  setOpenIndexes={setOpenIndexes}
                />
                <Box mt={-8} mb={-10}>
                  <img src='./CYPImages/socialworker.png' alt=' character' className='social' />
                </Box>
              </Box>

              {/* Social Worker Info */}
              <Grid item xs={12} mt={10}>
                <Typography sx={{ fontWeight: "bold", textAlign: "left" }}>My Social Worker's Name</Typography>
                <TextField
                  fullWidth
                  placeholder="Enter your Social Worker's name"
                  name='socialWorkerName'
                  value={values.socialWorkerName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.socialWorkerName && Boolean(errors.socialWorkerName)}
                  helperText={touched.socialWorkerName && errors.socialWorkerName}
                />
              </Grid>
              <Grid container spacing={2} mt={3} mb={5}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontWeight: "bold", textAlign: "left" }}>My Social Worker's Email</Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter your Social Worker's email"
                    name='socialWorkerEmail'
                    value={values.socialWorkerEmail}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.socialWorkerEmail && Boolean(errors.socialWorkerEmail)}
                    helperText={touched.socialWorkerEmail && errors.socialWorkerEmail}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontWeight: "bold", textAlign: "left" }}>
                    My Social Worker's Phone Number
                  </Typography>
                  <PhoneInput
                    country='gb'
                    value={values.socialWorkerPhone}
                    onChange={(phone) => setFieldValue("socialWorkerPhone", phone)}
                    onBlur={handleBlur}
                    inputProps={{
                      name: "socialWorkerPhone",
                      required: true,
                    }}
                    containerStyle={{ width: "100%" }}
                    inputStyle={{
                      width: "100%",
                      height: "55px",
                      fontSize: "16px",
                    }}
                  />
                  {touched.socialWorkerPhone && errors.socialWorkerPhone && (
                    <Typography color='error' variant='caption'>
                      {errors.socialWorkerPhone}
                    </Typography>
                  )}
                </Grid>
              </Grid>
              <Box
                sx={{
                  backgroundColor: "#E1F5FE",
                  height: "250px",
                  width: "400px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "10px",
                  marginLeft: "100px",
                }}
              >
                <Typography variant='h6' sx={{ textAlign: "center", fontWeight: "bold" }} color='info'>
                  Your social worker will come see you in your new home when they can. The adults in the home can remind
                  you ahead just so you stay informed on all plans about you.
                </Typography>
              </Box>
              <Box className='left-bottom-character'>
                <img src='./CYPImages/fatherson.png' alt='left character' className='left-bottom-character-image' />
              </Box>
              {/* Submit Button */}
              <Box mt={3}>
                <Grid container justifyContent='space-between' alignItems='center' mt={2}>
                  <Grid item>
                    <Button type='button' variant='contained' color='primary' onClick={prevStep}>
                      Previous
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button type='submit' variant='contained' color='primary'>
                      Next
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Form>
          </Box>
        )}
      </Formik>
      <Box className='bottom-education' mr={3}>
        <img src='./CYPImages/WormisStayingHome.png' alt='Bottom Character' className='bottom-education-image' />
      </Box>
    </Box>
  );
};

export default SocialWorkerInformation;
